import React from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    backgroundColor: "#F8FAFC",
    minHeight: "100%",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
  heading: {
    fontSize: "1.25rem",
    fontWeight: 600,
    color: "#273449",
    marginBottom: theme.spacing(1.5),
  },
  tableContainer: {
    boxShadow: "0 2px 10px rgba(39, 52, 73, 0.06)",
    border: "1px solid #E2E8F0",
    borderRadius: 12,
  },
  headerCell: {
    fontWeight: 700,
    fontSize: "0.85rem",
    backgroundColor: "#E6F7EF",
    color: "#065F46",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
  },
  bodyCell: {
    fontSize: "0.85rem",
    color: "#273449",
  },
  totalRow: {
    backgroundColor: "#F0FDF4",
    "& .MuiTableCell-root": {
      fontWeight: 700,
      fontSize: "0.85rem",
      color: "#065F46",
    },
  },
  emptyText: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: "#64748B",
  },
  stripList: {
    backgroundColor: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: 12,
    overflow: "hidden",
  },
  stripHeader: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(4.4rem, 1.15fr) minmax(4.2rem, 1fr) minmax(3.2rem, 0.75fr)",
    gap: 2,
    padding: "10px 8px",
    backgroundColor: "#E6F7EF",
    borderBottom: "1px solid #E2E8F0",
  },
  stripHeaderLabel: {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#065F46",
    whiteSpace: "nowrap",
  },
  stripRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(4.4rem, 1.15fr) minmax(4.2rem, 1fr) minmax(3.2rem, 0.75fr)",
    gap: 2,
    padding: "10px 8px",
    alignItems: "center",
    borderBottom: "1px solid #E2E8F0",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  stripTotalRow: {
    backgroundColor: "#F0FDF4",
  },
  stripBranch: {
    fontSize: "0.72rem",
    fontWeight: 600,
    color: "#273449",
    wordBreak: "break-word",
    lineHeight: 1.25,
  },
  stripValue: {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: "#065F46",
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  stripTotalLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "#065F46",
  },
  stripTotalValue: {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#065F46",
    textAlign: "right",
    whiteSpace: "nowrap",
  },
}));

const formatNumber = (value) => {
  if (value == null || isNaN(value)) return "0.00";
  return Number(value).toLocaleString("en-PK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatCovers = (value) => {
  if (value == null || isNaN(value)) return "0";
  return Math.round(Number(value)).toLocaleString("en-PK", {
    maximumFractionDigits: 0,
  });
};

const AvgSaleReport = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const reportData = useSelector(
    (state) => state.user?.branchWiseSale?.data,
  );
  const isFetching = useSelector(
    (state) => state.user?.branchWiseSale?.isFetching,
  );
  const error = useSelector((state) => state.user?.branchWiseSale?.error);

  const branches = reportData?.branches || [];

  return (
    <Box className={classes.root}>
      <Typography className={classes.heading}>
        Average Sale Report
      </Typography>

      {error && (
        <Typography color="error" style={{ marginBottom: 12 }}>
          {error}
        </Typography>
      )}

      {!isFetching && branches.length === 0 && !error && (
        <Typography className={classes.emptyText}>
          No data available for the selected date range.
        </Typography>
      )}

      {branches.length > 0 && isMobile && (
        <Box className={classes.stripList}>
          <Box className={classes.stripHeader}>
            <Typography className={classes.stripHeaderLabel}>Branch</Typography>
            <Typography
              className={classes.stripHeaderLabel}
              style={{ textAlign: "right", whiteSpace: "normal", lineHeight: 1.15 }}
            >
              Avg. Day Sale
            </Typography>
            <Typography
              className={classes.stripHeaderLabel}
              style={{ textAlign: "center", whiteSpace: "normal", lineHeight: 1.15 }}
            >
              Avg Covers
            </Typography>
            <Typography
              className={classes.stripHeaderLabel}
              style={{ textAlign: "right", whiteSpace: "normal", lineHeight: 1.15 }}
            >
              Chk Avg
            </Typography>
          </Box>

          {branches.map((branch) => (
            <Box key={branch.branchCode} className={classes.stripRow}>
              <Typography className={classes.stripBranch}>
                {branch.branchName}
              </Typography>
              <Typography className={classes.stripValue}>
                {formatNumber(branch.totalSale)}
              </Typography>
              <Typography className={classes.stripValue} style={{ textAlign: "center" }}>
                {formatCovers(branch.totalNoOfCover)}
              </Typography>
              <Typography className={classes.stripValue}>
                {formatNumber(branch.averagePerCover)}
              </Typography>
            </Box>
          ))}

          <Box className={`${classes.stripRow} ${classes.stripTotalRow}`}>
            <Typography className={classes.stripTotalLabel}>Avg Total</Typography>
            <Typography className={classes.stripTotalValue}>
              {formatNumber(reportData?.grandTotalSale)}
            </Typography>
            <Typography className={classes.stripTotalValue} style={{ textAlign: "center" }}>
              {formatCovers(reportData?.grandTotalCovers)}
            </Typography>
            <Typography className={classes.stripTotalValue}>
              {formatNumber(reportData?.grandAveragePerCover)}
            </Typography>
          </Box>
        </Box>
      )}

      {branches.length > 0 && !isMobile && (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table size="small" aria-label="average sale report">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>
                  Branch Name
                </TableCell>
                <TableCell className={classes.headerCell} align="right">
                  Avg. Day Sale
                </TableCell>
                <TableCell className={classes.headerCell} align="center">
                  Avg Covers
                </TableCell>
                <TableCell className={classes.headerCell} align="right">
                  Chk Avg
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {branches.map((branch) => (
                <TableRow key={branch.branchCode}>
                  <TableCell className={classes.bodyCell}>
                    {branch.branchName}
                  </TableCell>
                  <TableCell className={classes.bodyCell} align="right">
                    {formatNumber(branch.totalSale)}
                  </TableCell>
                  <TableCell className={classes.bodyCell} align="center">
                    {formatCovers(branch.totalNoOfCover)}
                  </TableCell>
                  <TableCell className={classes.bodyCell} align="right">
                    {formatNumber(branch.averagePerCover)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className={classes.totalRow}>
                <TableCell>Avg Total</TableCell>
                <TableCell align="right">
                  {formatNumber(reportData?.grandTotalSale)}
                </TableCell>
                <TableCell align="center">
                  {formatCovers(reportData?.grandTotalCovers)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(reportData?.grandAveragePerCover)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AvgSaleReport;
